import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Heart, Scale, ShoppingBag, Shield, Truck, ChevronLeft, ChevronRight, Check, MessageCircle, Send, Star, X, HardDrive, Cpu, Wifi, Watch as WatchIcon, Palette } from "lucide-react";
import { api, formatApiError, formatPrice, resolveImageUrl } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { useAuth } from "@/context/AuthContext";
import { getStoredUtm } from "@/lib/utm";
import ProductCard from "@/components/ProductCard";
import YandexPayBadge from "@/components/YandexPayBadge";
import YandexPaySplitWidget from "@/components/YandexPaySplitWidget";
import { toast } from "sonner";

function normalizeModelLabel(value) {
  return (value || "")
    .toLowerCase()
    .replace(/смартфон\s+/g, "")
    .replace(/\b\d+\s?(gb|гб|tb|тб)\b/gi, "")
    .replace(/\b(sim\s*\+\s*esim|esim|sim)\b/gi, "")
    // Parens usually hold noise to drop (SKU code, release year, English color
    // name) - but sometimes hold the ONLY marker of a different Apple Silicon
    // config ("iPad Air (M3, 2025)" vs "(M4, 2026)", "Mac Studio (M3 Ultra)" vs
    // "(M4 Max)", "Mac mini (M4)" vs "(M4 Pro)"). Blindly stripping the whole
    // group used to merge those into one "sibling" model, so a customer
    // switching storage/color could get silently bumped to a different chip
    // generation. Keep the chip marker if present, drop the rest of the group.
    .replace(/\(([^)]*)\)/g, (_, inner) => {
      const chip = inner.match(/\bm\d+\s*(?:pro|max|ultra)?\b/i);
      return chip ? ` ${chip[0]} ` : " ";
    })
    .replace(/[^a-zа-я0-9\s+]/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function getModelKey(product) {
  const explicitModel = (product?.model || "").trim();
  if (explicitModel) return normalizeModelLabel(explicitModel);
  const modelFromSpecs = product?.specs?.["Серия"] || product?.specs?.["Модель"];
  if (modelFromSpecs) return normalizeModelLabel(modelFromSpecs);
  return normalizeModelLabel(product?.name || "");
}

function getProductStorageOption(product) {
  const fromVariations = Array.from(new Set((product?.variations || []).map((v) => v?.memory || v?.storage).filter(Boolean)));
  if (fromVariations.length === 1) return fromVariations[0];
  if (fromVariations.length > 1) return fromVariations[0];
  return product?.specs?.["Встроенная память"] || product?.specs?.["Память"] || null;
}

function getProductRamOption(product) {
  const specs = product?.specs || {};
  const fromSpecs = specs["ОЗУ"] || specs["Оперативная память"] || specs["RAM"];
  if (fromSpecs) return String(fromSpecs).trim();
  const fromVariations = Array.from(new Set((product?.variations || []).map((v) => v?.ram).filter(Boolean)));
  if (fromVariations.length === 1) return fromVariations[0];
  if (fromVariations.length > 1) return fromVariations[0];
  return null;
}

function getProductBandOption(product) {
  const fromVariations = Array.from(new Set((product?.variations || []).map((v) => v?.band_type).filter(Boolean)));
  if (fromVariations.length === 1) return fromVariations[0];
  if (fromVariations.length > 1) return fromVariations[0];
  return null;
}

function normalizeConnectionLabel(value) {
  const raw = (value || "").toLowerCase().replaceAll("ё", "е").replace(/\s+/g, " ").trim();
  if (!raw) return null;
  const compact = raw.replace(/\s+/g, "");
  const hasEsim = compact.includes("esim");
  const hasSim = /(^|[^a-z0-9])sim([^a-z0-9]|$)/.test(raw) || compact.includes("dualsim");

  if (compact.includes("wi-fi") || compact.includes("wifi") || compact.includes("вайфай")) {
    if (compact.includes("cellular") || compact.includes("сотов") || compact.includes("lte") || compact.includes("5g")) {
      return "Wi-Fi + Cellular";
    }
    return "Wi-Fi";
  }

  if (compact.includes("sim+esim") || (hasSim && hasEsim)) {
    return "SIM + eSIM";
  }
  if (hasEsim) {
    return "eSIM";
  }
  if (hasSim) {
    return "SIM";
  }
  return value;
}

const KNOWN_CONNECTION_LABELS = new Set(["SIM + eSIM", "eSIM", "SIM", "Wi-Fi", "Wi-Fi + Cellular"]);

function getConnectionFromName(name) {
  const normalized = normalizeConnectionLabel(name || "");
  // normalizeConnectionLabel falls back to returning its input verbatim when no
  // SIM/eSIM/Wi-Fi token is found. That's fine for short structured values (a
  // spec field, a variation region), but here the input is the full product
  // name - unfiltered it would hand back the entire name as a bogus "connection"
  // value, unique per product, which silently breaks every cross-product
  // selector (color/storage/RAM) for categories like Mac/AirPods whose names
  // never mention SIM or Wi-Fi.
  if (normalized && KNOWN_CONNECTION_LABELS.has(normalized)) return normalized;
  return null;
}

function getProductConnectionOption(product) {
  const specs = product?.specs || {};
  const fromSpecs =
    specs["Связь"] ||
    specs["SIM"] ||
    specs["Тип SIM"] ||
    specs["Сим-карта"] ||
    specs["SIM-карта"];
  if (fromSpecs) return normalizeConnectionLabel(fromSpecs);

  const fromName = getConnectionFromName(product?.name || "");
  if (fromName) return fromName;

  const fromRegions = Array.from(new Set((product?.variations || []).map((v) => v?.region).filter(Boolean)));
  if (fromRegions.length === 1) return normalizeConnectionLabel(fromRegions[0]);
  if (fromRegions.length > 1) return normalizeConnectionLabel(fromRegions[0]);
  return null;
}

function getProductMainColor(product) {
  // The structured field wins: it's clean and always set for real catalog data.
  // Name-parsing is a last-resort fallback only - scraped names embed the color
  // lowercase mid-sentence and often drag in stray "#" separators or quote marks
  // ("...256Gb, фиолетовый # (MCJ84)" -> "фиолетовый #"), which used to leak
  // straight into the "Цвет (модель)" selector buttons.
  const fromVariation = (product?.variations || []).find((v) => v?.color)?.color;
  if (fromVariation) return fromVariation.trim();

  const fromSpecs = product?.specs?.["Цвет"];
  if (fromSpecs) return fromSpecs.trim();

  const name = product?.name || "";
  const byComma = name.match(/,\s*([^,(]+?)\s*(?:\(|\bSIM\b|\beSIM\b|$)/i)?.[1];
  if (byComma) return byComma.trim();

  const byParens = name.match(/\(([^)]+)\)/)?.[1];
  if (byParens) return byParens.trim();

  return null;
}

function stripParentheticalTranslation(value) {
  if (!value) return value;
  const stripped = value.replace(/\s*\([^)]*\)\s*$/, "").trim();
  return stripped || value;
}

function normalizeColorValue(value) {
  return (value || "")
    .toLowerCase()
    .replaceAll("ё", "е")
    .replace(/[^a-zа-я0-9]/gi, "");
}

function colorMatches(a, b) {
  const x = normalizeColorValue(a);
  const y = normalizeColorValue(b);
  if (!x || !y) return false;
  return x === y || x.includes(y) || y.includes(x);
}

function getProductMainColorHex(product) {
  const mainColor = getProductMainColor(product);
  const byColor = (product?.variations || []).find((v) => colorMatches(v?.color, mainColor));
  if (byColor?.color_hex) return byColor.color_hex;
  return (product?.variations || []).find((v) => v?.color_hex)?.color_hex || null;
}

function isWatchProduct(product) {
  if (!product) return false;
  if (product.category_slug === "watch") return true;
  return (product.variations || []).some((variation) => variation?.case_color || variation?.band_type || variation?.band_color);
}

function pickVariationByPreferredColor(product, preferredColor) {
  const variations = product?.variations || [];
  if (!variations.length) return null;
  if (!preferredColor) return variations[0];
  return variations.find((v) => colorMatches(v?.color, preferredColor)) || variations[0];
}

function getVariationImages(variation) {
  const images = Array.isArray(variation?.images) ? variation.images : [];
  const merged = [...images, variation?.image]
    .map((src) => String(src || "").trim())
    .filter(Boolean);
  return Array.from(new Set(merged));
}

function buildVariantSlug(baseSlug, variationId) {
  if (!baseSlug || !variationId) return baseSlug;
  return `${baseSlug}-v-${variationId}`;
}

function parseVariantSlug(rawSlug) {
  const m = (rawSlug || "").match(/^(.*)-v-([0-9a-fA-F-]{36})$/);
  if (!m) return { baseSlug: rawSlug, variationId: null };
  return { baseSlug: m[1], variationId: m[2] };
}

function ProductRail({ eyebrow, title, items, testId }) {
  const scrollerRef = useRef(null);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.9, 600), behavior: "smooth" });
  };

  if (!items.length) return null;

  return (
    <section className="mt-8 rounded-3xl border border-line bg-white p-6" data-testid={testId}>
      <div className="mb-5 flex items-end justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-soft">{eyebrow}</div>
          <div className="mt-2 text-2xl font-extrabold tracking-tightest text-ink">{title}</div>
        </div>
        {items.length > 2 && (
          <div className="hidden shrink-0 gap-2 sm:flex">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              className="grid h-10 w-10 place-items-center rounded-full border border-line text-ink transition-colors hover:bg-ink hover:text-white"
              aria-label="Назад"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              className="grid h-10 w-10 place-items-center rounded-full border border-line text-ink transition-colors hover:bg-ink hover:text-white"
              aria-label="Вперёд"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      <div ref={scrollerRef} className="flex gap-4 overflow-x-auto pb-2 scroll-smooth snap-x snap-mandatory">
        {items.map((item) => (
          <div key={item.id} className="w-[240px] shrink-0 snap-start">
            <ProductCard product={item} />
          </div>
        ))}
      </div>
    </section>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const parsedSlug = useMemo(() => parseVariantSlug(slug || ""), [slug]);
  const productSlugForApi = parsedSlug.baseSlug;
  const requestedVariationId = parsedSlug.variationId;
  const [product, setProduct] = useState(null);
  const [relatedModelProducts, setRelatedModelProducts] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedStorage, setSelectedStorage] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [imageIdx, setImageIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const touchStartXRef = useRef(null);
  const [reviews, setReviews] = useState([]);
  const [boughtTogether, setBoughtTogether] = useState([]);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [settings, setSettings] = useState({ telegram_url: "", whatsapp_url: "" });
  const [categories, setCategories] = useState([]);
  const [reviewForm, setReviewForm] = useState({ rating: 5, text: "" });
  const [reviewBusy, setReviewBusy] = useState(false);
  const [specsExpanded, setSpecsExpanded] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickBusy, setQuickBusy] = useState(false);
  const [quickForm, setQuickForm] = useState({ customer_name: "", phone: "" });
  const { addToCart, toggleWishlist, toggleCompare, isInWishlist, isInCompare } = useShop();
  const { isAuthed, user } = useAuth();
  const showLinkedModelSelectors = relatedModelProducts.length > 1;
  const watchMode = isWatchProduct(product);

  useEffect(() => {
    let active = true;
    setProduct(null);
    setRelatedModelProducts([]);
    setModelsLoading(true);
    api.get(`/catalog/products/${productSlugForApi}`).then(async (r) => {
      if (!active) return;
      const current = r.data;
      setProduct(current);
      const preferredColor = getProductMainColor(current);
      const preferredVariation = (current.variations || []).find((v) => v.id === requestedVariationId)
        || pickVariationByPreferredColor(current, preferredColor);
      setSelectedColor(preferredVariation?.color || null);
      setSelectedStorage(preferredVariation?.memory || preferredVariation?.storage || null);
      setSelectedRegion(preferredVariation?.region || null);
      setImageIdx(0);

      const modelKey = getModelKey(current);
      if (!modelKey) {
        setRelatedModelProducts([current]);
        setModelsLoading(false);
        return;
      }
      try {
        const { data } = await api.get("/catalog/products", {
          params: {
            category: current.category_slug,
            brand: current.brand_slug,
            limit: 500,
          },
        });
        if (!active) return;
        const items = Array.isArray(data) ? data : (data?.items || []);
        const sameModel = items.filter((item) => getModelKey(item) === modelKey);
        if (!sameModel.some((item) => item.id === current.id)) {
          sameModel.unshift(current);
        }
        setRelatedModelProducts(sameModel.length ? sameModel : [current]);
      } catch {
        if (!active) return;
        setRelatedModelProducts([current]);
      } finally {
        if (active) setModelsLoading(false);
      }
    }).catch(() => setProduct(false));
    api.get(`/catalog/products/${productSlugForApi}/reviews`).then((r) => setReviews(r.data.items || [])).catch(() => setReviews([]));
    api.get(`/catalog/products/${productSlugForApi}/recommendations`).then((r) => {
      if (!active) return;
      setBoughtTogether(r.data.bought_together || []);
      setSimilarProducts(r.data.similar || []);
    }).catch(() => {
      if (!active) return;
      setBoughtTogether([]);
      setSimilarProducts([]);
    });
    api.get("/settings").then((r) => setSettings(r.data || { telegram_url: "", whatsapp_url: "" })).catch(() => {});
    return () => {
      active = false;
    };
  }, [productSlugForApi, requestedVariationId]);

  useEffect(() => {
    api.get("/catalog/categories").then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  const categoryName = useMemo(() => {
    if (!product) return "";
    const match = categories.find((c) => c.slug === product.category_slug);
    return match?.name || product.category_slug;
  }, [categories, product]);

  useEffect(() => {
    if (!product) return;
    const title = `${product.name} — vip77.store`;
    document.title = title;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", product.description || `${product.name} — купить в vip77.store`);
    // Basic JSON-LD for product
    const ld = document.getElementById("ld-product-json");
    if (ld) ld.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "ld-product-json";
    try {
      script.text = JSON.stringify({
        "@context": "https://schema.org/",
        "@type": "Product",
        "name": product.name,
        "image": product.images || [],
        "description": product.description || "",
        "sku": product.sku || "",
        "brand": { "@type": "Brand", "name": product.brand || "" }
      });
      document.head.appendChild(script);
    } catch (e) {}
  }, [product]);

  useEffect(() => {
    const mine = reviews.find((x) => x.user_id === user?.id);
    if (mine) {
      setReviewForm({ rating: mine.rating || 5, text: mine.text || "" });
    } else {
      setReviewForm({ rating: 5, text: "" });
    }
  }, [reviews, user]);

  const colors = useMemo(() => {
    if (!product) return [];
    const m = new Map();
    for (const v of product.variations || []) {
      if (!m.has(v.color)) m.set(v.color, { color: v.color, hex: v.color_hex });
    }
    return Array.from(m.values());
  }, [product]);

  // "Стандарт" is a fallback placeholder for products that were never
  // actually sold in a choice of colors (a battery, hair clips) - showing a
  // fake single-swatch picker for it implies a choice that doesn't exist.
  const hasRealColor = !(colors.length === 1 && colors[0].color === "Стандарт");

  const storagesForColor = useMemo(() => {
    if (!product) return [];
    return (product.variations || [])
      .filter((v) => !selectedColor || colorMatches(v.color, selectedColor))
      .map((v) => v.memory || v.storage)
      .filter((v, i, a) => a.indexOf(v) === i);
  }, [product, selectedColor]);

  const regionsForSelection = useMemo(() => {
    if (!product) return [];
    return (product.variations || [])
      .filter((v) => (!selectedColor || colorMatches(v.color, selectedColor)) && (!selectedStorage || (v.memory || v.storage) === selectedStorage))
      .map((v) => v.region)
      .filter(Boolean)
      .filter((v, i, a) => a.indexOf(v) === i);
  }, [product, selectedColor, selectedStorage]);

  useEffect(() => {
    if (!regionsForSelection.length) return;
    if (!selectedRegion || !regionsForSelection.includes(selectedRegion)) {
      setSelectedRegion(regionsForSelection[0]);
    }
  }, [regionsForSelection, selectedRegion]);

  const activeVariation = useMemo(() => {
    if (!product) return null;
    return (product.variations || []).find((v) => (
      (!selectedColor || colorMatches(v.color, selectedColor)) &&
      (!selectedStorage || (v.memory || v.storage) === selectedStorage) &&
      (!selectedRegion || v.region === selectedRegion)
    )) || (product.variations || []).find((v) => (
      (!selectedColor || colorMatches(v.color, selectedColor)) &&
      (!selectedStorage || (v.memory || v.storage) === selectedStorage)
    )) || (product.variations || []).find((v) => (!selectedColor || colorMatches(v.color, selectedColor)));
  }, [product, selectedColor, selectedStorage, selectedRegion]);

  const displayedImages = useMemo(() => {
    if (!product) return [];
    const variationImages = getVariationImages(activeVariation);
    if (variationImages.length > 0) return variationImages;
    return Array.from(new Set((product.images || []).map((src) => String(src || "").trim()).filter(Boolean)));
  }, [product, activeVariation, selectedColor]);

  useEffect(() => {
    if (!displayedImages.length) {
      if (imageIdx !== 0) setImageIdx(0);
      return;
    }
    if (imageIdx >= displayedImages.length) {
      setImageIdx(0);
    }
  }, [displayedImages, imageIdx]);

  const lastVariationIdRef = useRef(null);
  useEffect(() => {
    const currentVariationId = activeVariation?.id || null;
    // Only jump the gallery when the VARIATION actually changed (a new color/
    // storage was picked). Without this guard, imageIdx was in this effect's
    // own dependency array, so clicking a thumbnail (which only sets imageIdx)
    // re-ran the effect, saw it no longer pointed at the variation's first
    // photo, and immediately snapped back to it - thumbnails looked broken.
    if (lastVariationIdRef.current === currentVariationId) return;
    lastVariationIdRef.current = currentVariationId;
    const activeImage = getVariationImages(activeVariation)[0];
    if (!activeImage || !displayedImages.length) return;
    const nextIdx = displayedImages.findIndex((src) => src === activeImage);
    if (nextIdx >= 0) {
      setImageIdx(nextIdx);
    }
  }, [activeVariation, displayedImages]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setLightboxOpen(false);
        return;
      }
      if (!displayedImages.length) return;
      if (e.key === "ArrowRight") {
        setImageIdx((idx) => (idx + 1) % displayedImages.length);
      }
      if (e.key === "ArrowLeft") {
        setImageIdx((idx) => (idx - 1 + displayedImages.length) % displayedImages.length);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxOpen, displayedImages.length]);

  const selectedModelStorage = useMemo(() => getProductStorageOption(product), [product]);
  const selectedModelRam = useMemo(() => getProductRamOption(product), [product]);
  const selectedModelConnection = useMemo(() => getProductConnectionOption(product), [product]);
  const selectedModelBand = useMemo(() => getProductBandOption(product), [product]);
  const selectedModelColor = useMemo(() => selectedColor || getProductMainColor(product), [product, selectedColor]);

  const relatedModelEntries = useMemo(() => (
    relatedModelProducts.map((item) => ({
      item,
      storage: getProductStorageOption(item),
      ram: getProductRamOption(item),
      connection: getProductConnectionOption(item),
      band: getProductBandOption(item),
      color: getProductMainColor(item),
    }))
  ), [relatedModelProducts]);

  const hasLinkedEntry = useCallback(({ storage, ram, connection, band, color }) => {
    return relatedModelEntries.some((entry) => {
      const storageOk = !storage || entry.storage === storage;
      const ramOk = !ram || entry.ram === ram;
      const connectionOk = !connection || entry.connection === connection;
      const bandOk = !band || entry.band === band;
      const colorOk = !color || colorMatches(entry.color, color);
      return storageOk && ramOk && connectionOk && bandOk && colorOk;
    });
  }, [relatedModelEntries]);

  const allModelStorageOptions = useMemo(() => (
    Array.from(new Set(relatedModelEntries.map((entry) => entry.storage).filter(Boolean)))
  ), [relatedModelEntries]);

  const allModelRamOptions = useMemo(() => (
    Array.from(new Set(relatedModelEntries.map((entry) => entry.ram).filter(Boolean)))
  ), [relatedModelEntries]);

  const allModelConnectionOptions = useMemo(() => (
    Array.from(new Set(relatedModelEntries.map((entry) => entry.connection).filter(Boolean)))
  ), [relatedModelEntries]);

  const allModelBandOptions = useMemo(() => (
    Array.from(new Set(relatedModelEntries.map((entry) => entry.band).filter(Boolean)))
  ), [relatedModelEntries]);

  const modelStorageOptions = useMemo(() => (
    allModelStorageOptions.filter((storageOption) => hasLinkedEntry({
      storage: storageOption,
      ram: selectedModelRam,
      connection: selectedModelConnection,
      band: selectedModelBand,
      color: selectedModelColor,
    }))
  ), [allModelStorageOptions, selectedModelRam, selectedModelConnection, selectedModelBand, selectedModelColor, hasLinkedEntry]);

  const modelRamOptions = useMemo(() => (
    allModelRamOptions.filter((ramOption) => hasLinkedEntry({
      storage: selectedModelStorage,
      ram: ramOption,
      connection: selectedModelConnection,
      band: selectedModelBand,
      color: selectedModelColor,
    }))
  ), [allModelRamOptions, selectedModelStorage, selectedModelConnection, selectedModelBand, selectedModelColor, hasLinkedEntry]);

  const modelConnectionOptions = useMemo(() => (
    allModelConnectionOptions.filter((connectionOption) => hasLinkedEntry({
      storage: selectedModelStorage,
      ram: selectedModelRam,
      connection: connectionOption,
      band: selectedModelBand,
      color: selectedModelColor,
    }))
  ), [allModelConnectionOptions, selectedModelStorage, selectedModelRam, selectedModelBand, selectedModelColor, hasLinkedEntry]);

  const modelBandOptions = useMemo(() => (
    allModelBandOptions.filter((bandOption) => hasLinkedEntry({
      storage: selectedModelStorage,
      ram: selectedModelRam,
      connection: selectedModelConnection,
      band: bandOption,
      color: selectedModelColor,
    }))
  ), [allModelBandOptions, selectedModelStorage, selectedModelRam, selectedModelConnection, selectedModelColor, hasLinkedEntry]);

  const modelColorOptions = useMemo(() => {
    const map = new Map();
    for (const entry of relatedModelEntries) {
      const mainColor = entry.color;
      if (!mainColor) continue;
      const existsForContext = hasLinkedEntry({
        storage: selectedModelStorage,
        ram: selectedModelRam,
        connection: selectedModelConnection,
        band: selectedModelBand,
        color: mainColor,
      });
      if (!existsForContext) continue;
      const key = normalizeColorValue(mainColor);
      if (!key || map.has(key)) continue;
      map.set(key, {
        name: mainColor,
        hex: getProductMainColorHex(entry.item),
      });
    }
    return Array.from(map.values());
  }, [relatedModelEntries, selectedModelStorage, selectedModelRam, selectedModelConnection, selectedModelBand, hasLinkedEntry]);

  // Nothing to pick from any section - render nothing rather than an empty
  // shell (happens when siblings only differ by an untracked attribute, e.g.
  // two AirPods 4 SKUs that differ solely by ANC on/off).
  const hasAnyModelOption = (
    modelStorageOptions.length > 1 ||
    modelRamOptions.length > 1 ||
    (!watchMode && modelConnectionOptions.length > 1) ||
    (watchMode && modelBandOptions.length > 1) ||
    modelColorOptions.length > 1
  );

  const findLinkedModelProduct = (storageOption, ramOption, connectionOption, colorOption, bandOption) => {
    return relatedModelProducts.find((item) => {
      const storage = getProductStorageOption(item);
      const ram = getProductRamOption(item);
      const connection = getProductConnectionOption(item);
      const band = getProductBandOption(item);
      const itemColor = getProductMainColor(item);
      const colorMatched = !colorOption || colorMatches(itemColor, colorOption);
      return (!storageOption || storage === storageOption) && (!ramOption || ram === ramOption) && (!connectionOption || connection === connectionOption) && (!bandOption || band === bandOption) && colorMatched;
    }) || relatedModelProducts.find((item) => {
      const connection = getProductConnectionOption(item);
      const itemColor = getProductMainColor(item);
      const colorMatched = !colorOption || colorMatches(itemColor, colorOption);
      return (!connectionOption || connection === connectionOption) && colorMatched;
    }) || relatedModelProducts.find((item) => {
      const storage = getProductStorageOption(item);
      const ram = getProductRamOption(item);
      const band = getProductBandOption(item);
      const itemColor = getProductMainColor(item);
      const colorMatched = !colorOption || colorMatches(itemColor, colorOption);
      return (!storageOption || storage === storageOption) && (!ramOption || ram === ramOption) && (!bandOption || band === bandOption) && colorMatched;
    }) || relatedModelProducts.find((item) => {
      const itemColor = getProductMainColor(item);
      return !colorOption || colorMatches(itemColor, colorOption);
    }) || relatedModelProducts[0] || null;
  };

  const pickVariationForProduct = (targetProduct, storageOption, ramOption, connectionOption, colorOption, bandOption) => {
    const vars = targetProduct?.variations || [];
    return vars.find((v) => (
      (!storageOption || (v.memory || v.storage) === storageOption) &&
      (!ramOption || v.ram === ramOption) &&
      (!connectionOption || normalizeConnectionLabel(v.region || connectionOption) === normalizeConnectionLabel(connectionOption)) &&
      (!bandOption || v.band_type === bandOption) &&
      (!colorOption || colorMatches(v.color, colorOption))
    )) || vars.find((v) => (
      (!storageOption || (v.memory || v.storage) === storageOption) &&
      (!ramOption || v.ram === ramOption) &&
      (!bandOption || v.band_type === bandOption) &&
      (!colorOption || colorMatches(v.color, colorOption))
    )) || vars.find((v) => (!colorOption || colorMatches(v.color, colorOption))) || vars[0] || null;
  };

  const navigateToLinkedProduct = (storageOption, ramOption, connectionOption, colorOption, bandOption) => {
    const target = findLinkedModelProduct(storageOption, ramOption, connectionOption, colorOption, bandOption);
    if (!target) return;
    const targetVariation = pickVariationForProduct(target, storageOption, ramOption, connectionOption, colorOption, bandOption);
    const targetUrl = `/product/${buildVariantSlug(target.slug, targetVariation?.id)}`;
    if (target.slug === productSlugForApi) {
      const local = targetVariation || pickVariationByPreferredColor(product, colorOption);
      if (local) {
        setSelectedColor(local.color || null);
        setSelectedStorage(local.memory || local.storage || null);
        setSelectedRegion(local.region || null);
        const localUrl = `/product/${buildVariantSlug(target.slug, local.id)}`;
        if (slug !== buildVariantSlug(target.slug, local.id)) {
          navigate(localUrl, { replace: true });
        }
      }
      return;
    }
    navigate(targetUrl);
  };

  if (product === null) {
    return (
      <div className="grid min-h-[60vh] place-items-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-ink border-t-transparent" />
      </div>
    );
  }
  if (product === false) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="text-3xl font-bold text-ink">Товар не найден</h1>
        <Link to="/" className="mt-6 inline-block rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white">На главную</Link>
      </div>
    );
  }

  const inWish = isInWishlist(product.id);
  const inCmp = isInCompare(product.id);
  const specsEntries = Object.entries(product.specs || {});
  const visibleSpecsEntries = specsExpanded ? specsEntries : specsEntries.slice(0, 5);

  const submitReview = async () => {
    if (!isAuthed || !reviewForm.text.trim()) return;
    setReviewBusy(true);
    try {
      const { data } = await api.post(`/catalog/products/${productSlugForApi}/reviews`, {
        rating: Number(reviewForm.rating),
        text: reviewForm.text.trim(),
      });
      setReviews((prev) => [data, ...prev.filter((item) => item.id !== data.id)]);
    } finally {
      setReviewBusy(false);
    }
  };

  const submitQuickOrder = async (e) => {
    e.preventDefault();
    if (!quickForm.customer_name.trim() || !quickForm.phone.trim()) return;
    setQuickBusy(true);
    try {
      await api.post("/quick-orders", {
        customer_name: quickForm.customer_name.trim(),
        phone: quickForm.phone.trim(),
        product_id: product.id,
        product_slug: product.slug,
        variation_id: activeVariation?.id || null,
        source_page: typeof window !== "undefined" ? window.location.pathname : "/product",
        utm: getStoredUtm() || undefined,
      });
      toast.success("Заявка отправлена. Менеджер скоро свяжется с вами.");
      setQuickOpen(false);
      setQuickForm({ customer_name: "", phone: "" });
    } catch (err) {
      toast.error(formatApiError(err, "Не удалось отправить заявку"));
    } finally {
      setQuickBusy(false);
    }
  };

  return (
    <div data-testid="product-detail-page" className="mx-auto max-w-7xl px-4 py-10 pb-28 md:pb-10 lg:px-8">
      <div className="mb-6 flex items-center gap-2 text-xs text-muted">
        <Link to="/" className="hover:underline">Главная</Link>
        <ChevronRight size={12} />
        <Link to={`/catalog/${product.category_slug}`} className="hover:underline">
          {categoryName}
        </Link>
        <ChevronRight size={12} />
        <span className="truncate text-ink">{product.name}</span>
      </div>

      <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
        {/* Gallery */}
        <div className="md:col-span-7">
          <div className="md:sticky md:top-24">
            <div className="overflow-hidden rounded-3xl border border-line bg-white">
              <button
                type="button"
                onClick={() => displayedImages.length && setLightboxOpen(true)}
                className="grid aspect-square w-full cursor-zoom-in place-items-center"
              >
                <img
                  src={resolveImageUrl(displayedImages[imageIdx] || displayedImages[0])}
                  alt={product.name}
                  className="h-full w-full object-contain p-6"
                />
              </button>
            </div>
            {displayedImages.length > 1 && (
              <div className="mt-3 flex gap-3 overflow-x-auto pb-1">
                {displayedImages.map((src, i) => (
                  <button
                    key={i}
                    data-testid={`gallery-thumb-${i}`}
                    onClick={() => setImageIdx(i)}
                    className={`h-16 w-16 shrink-0 overflow-hidden rounded-xl border transition-colors ${
                      i === imageIdx ? "border-ink" : "border-line hover:border-ink"
                    }`}
                  >
                    <img src={resolveImageUrl(src)} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="md:col-span-5">
          <div className="rise-in">
            <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-soft">
              {categoryName} · {product.brand_slug}
            </div>
            <h1 className="mt-2 text-3xl font-extrabold tracking-tightest text-ink md:text-4xl">
              {product.name}
            </h1>
            {product.subtitle && (
              <p className="mt-2 text-base text-muted">{product.subtitle}</p>
            )}

            <div className="mt-6 flex items-baseline gap-3">
              <div className="text-4xl font-extrabold tracking-tightest text-ink">
                {formatPrice(activeVariation?.price ?? product.min_price)}
              </div>
              {activeVariation?.old_price && (
                <div className="text-lg text-muted line-through">{formatPrice(activeVariation.old_price)}</div>
              )}
            </div>
            <p className="mt-1.5 text-xs text-muted">Цена указана за наличный расчёт</p>
            <YandexPayBadge amount={activeVariation?.price ?? product.min_price} className="mt-4" />

            {modelsLoading && (
              <div className="mt-7 animate-pulse space-y-5 rounded-2xl border border-line bg-paper p-5">
                <div>
                  <div className="mb-3 h-2.5 w-16 rounded-full bg-line" />
                  <div className="flex flex-wrap gap-2">
                    <div className="h-9 w-16 rounded-full bg-line/80" />
                    <div className="h-9 w-16 rounded-full bg-line/80" />
                    <div className="h-9 w-16 rounded-full bg-line/80" />
                  </div>
                </div>
                <div>
                  <div className="mb-3 h-2.5 w-12 rounded-full bg-line" />
                  <div className="flex flex-wrap gap-2">
                    <div className="h-9 w-24 rounded-full bg-line/80" />
                    <div className="h-9 w-24 rounded-full bg-line/80" />
                  </div>
                </div>
              </div>
            )}

            {!modelsLoading && showLinkedModelSelectors && hasAnyModelOption && (
              <div className="mt-7 divide-y divide-line/70 rounded-2xl border border-line bg-gradient-to-b from-paper to-white p-5 shadow-sm">
                {modelStorageOptions.length > 1 && (
                  <div className="py-4 first:pt-0 last:pb-0">
                    <div className="mb-3 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-muted-soft">
                      <HardDrive size={13} className="text-muted" />
                      {watchMode ? "Размер" : "Память"}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {modelStorageOptions.map((storageOption) => (
                        <button
                          key={storageOption}
                          data-testid={`linked-storage-${storageOption}`}
                          onClick={() => navigateToLinkedProduct(storageOption, selectedModelRam, selectedModelConnection, selectedModelColor, selectedModelBand)}
                          className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
                            selectedModelStorage === storageOption
                              ? "border-ink bg-ink text-white shadow-sm"
                              : "border-line bg-white text-ink hover:-translate-y-px hover:border-ink hover:shadow-sm"
                          }`}
                        >
                          {storageOption}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {modelRamOptions.length > 1 && (
                  <div className="py-4 first:pt-0 last:pb-0">
                    <div className="mb-3 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-muted-soft">
                      <Cpu size={13} className="text-muted" />
                      ОЗУ
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {modelRamOptions.map((ramOption) => (
                        <button
                          key={ramOption}
                          data-testid={`linked-ram-${ramOption}`}
                          onClick={() => navigateToLinkedProduct(selectedModelStorage, ramOption, selectedModelConnection, selectedModelColor, selectedModelBand)}
                          className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
                            selectedModelRam === ramOption
                              ? "border-ink bg-ink text-white shadow-sm"
                              : "border-line bg-white text-ink hover:-translate-y-px hover:border-ink hover:shadow-sm"
                          }`}
                        >
                          {ramOption}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {!watchMode && modelConnectionOptions.length > 1 && (
                  <div className="py-4 first:pt-0 last:pb-0">
                    <div className="mb-3 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-muted-soft">
                      <Wifi size={13} className="text-muted" />
                      Связь
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {modelConnectionOptions.map((connectionOption) => (
                        <button
                          key={connectionOption}
                          data-testid={`linked-connection-${connectionOption}`}
                          onClick={() => navigateToLinkedProduct(selectedModelStorage, selectedModelRam, connectionOption, selectedModelColor, selectedModelBand)}
                          className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
                            selectedModelConnection === connectionOption
                              ? "border-ink bg-ink text-white shadow-sm"
                              : "border-line bg-white text-ink hover:-translate-y-px hover:border-ink hover:shadow-sm"
                          }`}
                        >
                          {connectionOption}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {watchMode && modelBandOptions.length > 1 && (
                  <div className="py-4 first:pt-0 last:pb-0">
                    <div className="mb-3 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-muted-soft">
                      <WatchIcon size={13} className="text-muted" />
                      Ремешок
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {modelBandOptions.map((bandOption) => (
                        <button
                          key={bandOption}
                          data-testid={`linked-band-${bandOption}`}
                          onClick={() => navigateToLinkedProduct(selectedModelStorage, selectedModelRam, selectedModelConnection, selectedModelColor, bandOption)}
                          className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
                            selectedModelBand === bandOption
                              ? "border-ink bg-ink text-white shadow-sm"
                              : "border-line bg-white text-ink hover:-translate-y-px hover:border-ink hover:shadow-sm"
                          }`}
                        >
                          {bandOption}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {modelColorOptions.length > 1 && (
                  <div className="py-4 first:pt-0 last:pb-0">
                    <div className="mb-3 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-muted-soft">
                      <Palette size={13} className="text-muted" />
                      Цвет
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {modelColorOptions.map((colorOption) => (
                        <button
                          key={colorOption.name}
                          data-testid={`linked-color-${colorOption.name}`}
                          onClick={() => navigateToLinkedProduct(selectedModelStorage, selectedModelRam, selectedModelConnection, colorOption.name, selectedModelBand)}
                          className={`rounded-full border px-3.5 py-2 text-sm font-semibold transition-all ${
                            colorMatches(selectedModelColor, colorOption.name)
                              ? "border-ink bg-ink text-white shadow-sm"
                              : "border-line bg-white text-ink hover:-translate-y-px hover:border-ink hover:shadow-sm"
                          }`}
                        >
                          <span className="inline-flex items-center gap-2">
                            <span
                              className="h-4 w-4 rounded-full border border-black/10 shadow-inner"
                              style={{ backgroundColor: colorOption.hex || "#ddd" }}
                            />
                            {colorOption.name}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Colors */}
            {!modelsLoading && !showLinkedModelSelectors && hasRealColor && (
              <div className="mt-8">
              <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-muted-soft">{watchMode ? "Цвет ремешка" : "Цвет"}: <span className="text-ink">{selectedColor}</span></div>
              <div className="flex flex-wrap gap-2">
                {colors.map((c) => (
                  <button
                    key={c.color}
                    data-testid={`color-${c.color}`}
                    onClick={() => {
                      setSelectedColor(c.color);
                      const first = product.variations.find((v) => colorMatches(v.color, c.color));
                      if (first) setSelectedStorage(first.memory || first.storage);
                      if (first) setSelectedRegion(first.region || null);
                    }}
                    aria-label={c.color}
                    className={`grid h-10 w-10 place-items-center rounded-full transition-transform hover:scale-105 ${
                      selectedColor === c.color ? "ring-2 ring-ink ring-offset-2" : "ring-1 ring-line"
                    }`}
                    style={{ background: c.hex }}
                  >
                    {selectedColor === c.color && <Check size={14} className="text-white mix-blend-difference" />}
                  </button>
                ))}
              </div>
              </div>
            )}

            {/* Storage */}
            {!modelsLoading && !showLinkedModelSelectors && storagesForColor.some(Boolean) && (
              <div className="mt-6">
                <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-muted-soft">{watchMode ? "Размер" : "Память"}</div>
                <div className="flex flex-wrap gap-2">
                  {storagesForColor.filter(Boolean).map((s) => (
                    <button
                      key={s}
                      data-testid={`storage-${s}`}
                      onClick={() => setSelectedStorage(s)}
                      className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                        selectedStorage === s
                          ? "border-ink bg-ink text-white"
                          : "border-line bg-white text-ink hover:border-ink"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* SIM / eSIM */}
            {!modelsLoading && !showLinkedModelSelectors && regionsForSelection.some(Boolean) && (
              <div className="mt-6">
                <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-muted-soft">Связь</div>
                <div className="flex flex-wrap gap-2">
                  {regionsForSelection.map((region) => (
                    <button
                      key={region}
                      data-testid={`region-${region}`}
                      onClick={() => setSelectedRegion(region)}
                      className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                        selectedRegion === region
                          ? "border-ink bg-ink text-white"
                          : "border-line bg-white text-ink hover:border-ink"
                      }`}
                    >
                      {region}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Meta chips */}
            {activeVariation && (
              <div className="mt-6 flex flex-wrap gap-2">
                {watchMode && activeVariation.case_color && (
                  <span className="rounded-full border border-line bg-white px-3 py-1 text-xs text-ink">Цвет корпуса: {stripParentheticalTranslation(activeVariation.case_color)}</span>
                )}
                {watchMode && activeVariation.band_type && (
                  <span className="rounded-full border border-line bg-white px-3 py-1 text-xs text-ink">Тип ремешка: {activeVariation.band_type}</span>
                )}
                {watchMode && activeVariation.band_color && activeVariation.band_color !== activeVariation.color && (
                  <span className="rounded-full border border-line bg-white px-3 py-1 text-xs text-ink">Цвет ремешка: {stripParentheticalTranslation(activeVariation.band_color)}</span>
                )}
                {activeVariation.region && (
                  <span className="rounded-full border border-line bg-white px-3 py-1 text-xs text-ink">Связь: {activeVariation.region}</span>
                )}
                {activeVariation.warranty && (
                  <span className="rounded-full border border-line bg-white px-3 py-1 text-xs text-ink">Гарантия: {activeVariation.warranty}</span>
                )}
                <span className={`rounded-full border px-3 py-1 text-xs ${activeVariation.stock > 0 ? "border-success/40 bg-success/10 text-success" : activeVariation.is_preorder ? "border-accent/40 bg-accent/10 text-accent" : "border-danger/40 bg-danger/10 text-danger"}`}>
                  {activeVariation.stock > 0
                    ? `В наличии: ${activeVariation.stock}`
                    : activeVariation.is_preorder
                    ? activeVariation.preorder_note || "Предзаказ"
                    : "Нет в наличии"}
                </span>
              </div>
            )}

            {/* CTA */}
            <div className="mt-8 flex flex-col gap-3 md:flex-row md:flex-wrap">
              <div className="flex gap-3 md:flex-1">
                <button
                  data-testid="add-to-cart-btn"
                  disabled={!activeVariation || (activeVariation.stock <= 0 && !activeVariation.is_preorder)}
                  onClick={() => addToCart(product, activeVariation.id, 1)}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-ink px-6 py-4 text-sm font-semibold text-white transition-colors hover:bg-ink-soft disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ShoppingBag size={16} /> В корзину
                </button>
                <button
                  type="button"
                  data-testid="quick-buy-btn"
                  disabled={!activeVariation || (activeVariation.stock <= 0 && !activeVariation.is_preorder)}
                  onClick={() => setQuickOpen(true)}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-ink px-6 py-4 text-sm font-semibold text-ink transition-colors hover:bg-ink hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Купить в 1 клик
                </button>
              </div>
              <div className="flex gap-3">
                <button
                  data-testid="wishlist-btn"
                  onClick={() => toggleWishlist(product.id)}
                  className={`grid h-14 w-14 shrink-0 place-items-center rounded-full border transition-colors ${
                    inWish ? "border-ink bg-ink text-white" : "border-line bg-white text-ink hover:bg-ink hover:text-white"
                  }`}
                  aria-label="Избранное"
                >
                  <Heart size={18} fill={inWish ? "currentColor" : "none"} />
                </button>
                <button
                  data-testid="compare-btn"
                  onClick={() => toggleCompare(product)}
                  className={`grid h-14 w-14 shrink-0 place-items-center rounded-full border transition-colors ${
                    inCmp ? "border-ink bg-ink text-white" : "border-line bg-white text-ink hover:bg-ink hover:text-white"
                  }`}
                  aria-label="К сравнению"
                >
                  <Scale size={18} />
                </button>
              </div>
            </div>

            <YandexPaySplitWidget amount={activeVariation?.price ?? product.min_price} className="mt-6" />

            {/* Trust badges */}
            <div className="mt-6 grid grid-cols-2 gap-3 text-sm text-ink">
              <div className="flex items-center gap-2 rounded-xl border border-line bg-white px-3 py-2">
                <Shield size={16} /> Оригинал 100%
              </div>
              <div className="flex items-center gap-2 rounded-xl border border-line bg-white px-3 py-2">
                <Truck size={16} /> Доставка по РФ
              </div>
            </div>

            {/* Description */}
            {product.description && (
              <p className="mt-8 text-sm leading-relaxed text-muted">{product.description}</p>
            )}

            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {settings.telegram_url && (
                <a
                  href={settings.telegram_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-line bg-white px-5 py-3 text-sm font-semibold text-ink transition-colors hover:border-ink hover:bg-ink hover:text-white"
                >
                  <Send size={16} /> Связаться в Telegram
                </a>
              )}
              {settings.whatsapp_url && (
                <a
                  href={settings.whatsapp_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-line bg-white px-5 py-3 text-sm font-semibold text-ink transition-colors hover:border-ink hover:bg-ink hover:text-white"
                >
                  <MessageCircle size={16} /> Связаться в WhatsApp
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {lightboxOpen && displayedImages.length > 0 && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 p-4 md:p-8"
          onClick={() => setLightboxOpen(false)}
          onTouchStart={(e) => {
            touchStartXRef.current = e.touches[0].clientX;
          }}
          onTouchEnd={(e) => {
            const startX = touchStartXRef.current;
            touchStartXRef.current = null;
            if (startX == null || !displayedImages.length) return;
            const deltaX = e.changedTouches[0].clientX - startX;
            if (Math.abs(deltaX) < 40) return;
            if (deltaX < 0) {
              setImageIdx((idx) => (idx + 1) % displayedImages.length);
            } else {
              setImageIdx((idx) => (idx - 1 + displayedImages.length) % displayedImages.length);
            }
          }}
        >
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full border border-white/30 text-white hover:bg-white/10"
          >
            <X size={18} />
          </button>

          {displayedImages.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setImageIdx((idx) => (idx - 1 + displayedImages.length) % displayedImages.length);
                }}
                className="absolute left-4 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border border-white/30 text-white hover:bg-white/10"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setImageIdx((idx) => (idx + 1) % displayedImages.length);
                }}
                className="absolute right-4 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border border-white/30 text-white hover:bg-white/10"
              >
                <ChevronRight size={20} />
              </button>
            </>
          )}

          <div className="grid h-full place-items-center">
            <img
              src={resolveImageUrl(displayedImages[imageIdx])}
              alt={product.name}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[90vh] w-auto max-w-[95vw] rounded-2xl object-contain"
            />
          </div>
        </div>
      )}

      <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-[1.1fr_0.9fr]">
        {specsEntries.length > 0 && (
          <section className="rounded-3xl border border-line bg-white p-6" data-testid="product-specs-section">
            <div className="mb-5 text-[11px] font-bold uppercase tracking-[0.24em] text-muted-soft">Характеристики</div>
            <div className="divide-y divide-line">
              {visibleSpecsEntries.map(([k, v]) => (
                <div key={k} className="grid gap-2 py-4 md:grid-cols-[220px_1fr] md:gap-6">
                  <div className="text-sm font-semibold text-muted">{k}</div>
                  <div className="text-sm text-ink">{v}</div>
                </div>
              ))}
            </div>
            {specsEntries.length > 5 && (
              <button
                type="button"
                onClick={() => setSpecsExpanded((prev) => !prev)}
                className="mt-4 rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-ink transition-colors hover:border-ink"
              >
                {specsExpanded ? "Свернуть" : `Показать все (${specsEntries.length - 5})`}
              </button>
            )}
          </section>
        )}

        <section className="rounded-3xl border border-line bg-white p-6" data-testid="product-reviews-section">
          <div className="mb-5 flex items-center justify-between gap-4">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-soft">Отзывы</div>
              <div className="mt-2 text-2xl font-extrabold tracking-tightest text-ink">{reviews.length} отзывов</div>
            </div>
            <div className="rounded-full bg-paper px-4 py-2 text-sm font-semibold text-ink">
              {product.rating ? `${product.rating} / 5` : "Нет оценки"}
            </div>
          </div>

          {isAuthed ? (
            <div className="mb-6 rounded-2xl border border-line bg-paper p-4">
              <div className="mb-3 text-sm font-semibold text-ink">Ваш отзыв</div>
              <div className="mb-3 flex gap-2">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setReviewForm((prev) => ({ ...prev, rating: value }))}
                    className="text-warning"
                    aria-label={`Оценка ${value}`}
                  >
                    <Star size={18} fill={value <= reviewForm.rating ? "currentColor" : "none"} />
                  </button>
                ))}
              </div>
              <textarea
                data-testid="review-text"
                rows={4}
                value={reviewForm.text}
                onChange={(e) => setReviewForm((prev) => ({ ...prev, text: e.target.value }))}
                placeholder="Напишите впечатление о товаре"
                className="w-full rounded-2xl border border-line bg-white px-4 py-3 text-sm outline-none focus:border-ink"
              />
              <button
                type="button"
                onClick={submitReview}
                disabled={reviewBusy || !reviewForm.text.trim()}
                className="mt-3 inline-flex items-center rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-ink-soft disabled:opacity-50"
              >
                {reviewBusy ? "Сохраняем…" : "Сохранить отзыв"}
              </button>
            </div>
          ) : (
            <div className="mb-6 rounded-2xl border border-dashed border-line px-4 py-3 text-sm text-muted">
              Чтобы оставить отзыв, войдите в аккаунт.
            </div>
          )}

          <div className="space-y-4">
            {reviews.length === 0 && (
              <div className="rounded-2xl border border-dashed border-line px-4 py-8 text-center text-sm text-muted">
                Пока нет отзывов.
              </div>
            )}
            {reviews.map((review) => (
              <article key={review.id} className="rounded-2xl border border-line p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-semibold text-ink">{review.user_name}</div>
                    <div className="mt-1 flex gap-1 text-warning">
                      {[1, 2, 3, 4, 5].map((value) => (
                        <Star key={value} size={14} fill={value <= review.rating ? "currentColor" : "none"} />
                      ))}
                    </div>
                  </div>
                  <div className="text-xs text-muted">{new Date(review.updated_at || review.created_at).toLocaleDateString("ru-RU")}</div>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted">{review.text}</p>
              </article>
            ))}
          </div>
        </section>
      </div>

      <ProductRail
        testId="bought-together-section"
        eyebrow="На основе заказов"
        title="С этим товаром покупают"
        items={boughtTogether}
      />
      <ProductRail
        testId="similar-products-section"
        eyebrow="Может понравиться"
        title="Похожие товары"
        items={similarProducts}
      />

      {activeVariation && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-white/95 px-4 py-3 backdrop-blur md:hidden">
          <div className="mx-auto flex max-w-7xl items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="truncate text-xs text-muted">{product.name}</div>
              <div className="text-lg font-extrabold tracking-tight text-ink">
                {formatPrice(activeVariation?.price ?? product.min_price)}
              </div>
            </div>
            <button
              data-testid="add-to-cart-sticky-mobile"
              disabled={activeVariation.stock <= 0 && !activeVariation.is_preorder}
              onClick={() => addToCart(product, activeVariation.id, 1)}
              className="inline-flex items-center justify-center rounded-full bg-ink px-5 py-3 text-sm font-semibold text-white disabled:opacity-40"
            >
              В корзину
            </button>
          </div>
        </div>
      )}

      {quickOpen && (
        <div className="fixed inset-0 z-[90] p-4" data-testid="quick-buy-modal">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            onClick={() => setQuickOpen(false)}
            aria-label="Закрыть"
          />
          <div className="relative mx-auto mt-[8vh] w-full max-w-md rounded-2xl border border-line bg-white p-5 shadow-2xl">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-soft">Быстрый заказ</div>
                <h3 className="mt-1 text-xl font-extrabold tracking-tight text-ink">Купить в 1 клик</h3>
                <p className="mt-1 text-sm text-muted">{product.name}</p>
              </div>
              <button
                type="button"
                onClick={() => setQuickOpen(false)}
                className="grid h-8 w-8 place-items-center rounded-full border border-line text-ink hover:bg-black/5"
                aria-label="Закрыть"
              >
                <X size={14} />
              </button>
            </div>

            <form onSubmit={submitQuickOrder} className="space-y-3">
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-muted">Имя *</span>
                <input
                  required
                  value={quickForm.customer_name}
                  onChange={(e) => setQuickForm((prev) => ({ ...prev, customer_name: e.target.value }))}
                  className="w-full rounded-lg border border-line bg-white px-3 py-2.5 text-sm outline-none focus:border-ink"
                />
              </label>

              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-muted">Телефон *</span>
                <input
                  required
                  value={quickForm.phone}
                  onChange={(e) => setQuickForm((prev) => ({ ...prev, phone: e.target.value }))}
                  placeholder="+7 (___) ___-__-__"
                  className="w-full rounded-lg border border-line bg-white px-3 py-2.5 text-sm outline-none focus:border-ink"
                />
              </label>

              <button
                type="submit"
                disabled={quickBusy}
                className="w-full rounded-full bg-ink py-3 text-sm font-semibold text-white transition-colors hover:bg-ink-soft disabled:opacity-50"
              >
                {quickBusy ? "Отправляем…" : "Отправить заявку"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
